import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useBooking } from "../../../context/BookingContext";
import {
  getCustomerProfile,
  getAllStringsFromInventory,
  getExpressSettingsApi,
} from "../../../api";
import { getDubaiDate, getDubaiTodayYMD } from "../../../utils/date";
import styles from "./CustomerBookingForm.module.css";
import useAuthGuard from "../../../hooks/useAuthGuard";

const CustomerBookingForm = () => {
  useAuthGuard();
  const navigate = useNavigate();
  const location = useLocation();
  const { address: contextAddress } = useBooking();
  const [validationError, setValidationError] = useState("");

  const {
    customerName: prevCustomerName,
    bookingDate: prevBookingDate,
    racketName: prevRacketName,
    previousBookings = [],
    freeServices = 0,
    usedServices = 0,
    isFree = false,
    customerId: stateCustomerId,
    rp_id = null,

    pickupService: prevPickupService = null,
    instoreLocation: prevInstoreLocation = null,
    address: prevAddress = null,

    // lock string type across bookings ("Regular" or "Express")
    stringTypeConstraint = null,
    lockServiceSelection = false,
  } = location.state || {};

  const [expressSettings, setExpressSettings] = useState({
    is_express_enabled: true,
    marina_express_enabled: false,
    jumeirah_express_enabled: true,
    dubaihills_express_enabled: false,
    cutoff_hour: 17,
  });

  useEffect(() => {
    getExpressSettingsApi()
      .then((res) => {
        if (res?.data) setExpressSettings(res.data);
      })
      .catch((err) => {
        console.error("Could not fetch express settings:", err);
      });
  }, []);

  // Express cutoff & location logic (strictly in Asia/Dubai timezone)
  const EXPRESS_CUTOFF_HOUR = expressSettings.cutoff_hour ?? 17;
  const afterExpressCutoff = (ymd) => {
    const dubai = getDubaiDate();
    return (ymd === dubai.ymd || !ymd) && dubai.hour >= EXPRESS_CUTOFF_HOUR;
  };

  const getCurrentDate = () => getDubaiTodayYMD();

  const [customerName, setCustomerName] = useState(
    prevCustomerName || "Loading..."
  );
  const [customerId, setCustomerId] = useState(stateCustomerId || null);
  const [bookingDate, setBookingDate] = useState(
    prevBookingDate || getCurrentDate()
  );
  const [racketNameInput, setRacketNameInput] = useState(prevRacketName || "");

  // Service selection state
  const [serviceSpeed, setServiceSpeed] = useState(stringTypeConstraint || "");
  const [pickupService, setPickupService] = useState(prevPickupService || "");
  const [instoreLocation, setInstoreLocation] = useState(
    prevInstoreLocation || ""
  );

  const isMarina = pickupService === "in-store" && instoreLocation?.toLowerCase().includes("marina");
  const isMarinaExpressDisabled = isMarina && !expressSettings.marina_express_enabled;
  const isDubaiHills = pickupService === "in-store" && instoreLocation?.toLowerCase().includes("dubai");
  const isDubaiHillsExpressDisabled = isDubaiHills && !expressSettings.dubaihills_express_enabled;
  const isGlobalExpressDisabled = !expressSettings.is_express_enabled;
  const isExpressDisabledForForm = afterExpressCutoff(bookingDate) || isMarinaExpressDisabled || isDubaiHillsExpressDisabled || isGlobalExpressDisabled;

  const getExpressDisabledReason = () => {
    if (isGlobalExpressDisabled) return "Express service is currently disabled";
    if (isMarinaExpressDisabled) return "Express service not available at Marina location";
    if (isDubaiHillsExpressDisabled) return "Express service not available at Dubai Hills location";
    if (afterExpressCutoff(bookingDate)) {
      const formattedHour = EXPRESS_CUTOFF_HOUR > 12 ? `${EXPRESS_CUTOFF_HOUR - 12} PM` : `${EXPRESS_CUTOFF_HOUR} AM`;
      return `Express service not available after ${formattedHour} for today`;
    }
    return "";
  };

  // Auto-reset Express to Regular if Express becomes disabled
  useEffect(() => {
    if (isExpressDisabledForForm && serviceSpeed === "Express") {
      setServiceSpeed("Regular");
      setSelectedStringType("Regular");
    }
  }, [isExpressDisabledForForm, serviceSpeed]);

  const [selectedStringId, setSelectedStringId] = useState(null);
  const [selectedStringType, setSelectedStringType] = useState(
    stringTypeConstraint || ""
  );
  const [_totalCost, setTotalCost] = useState(""); // eslint-disable-line no-unused-vars
  const [strings, setStrings] = useState([]);
  const [loadingStrings, setLoadingStrings] = useState(true);
  const [apiError, setApiError] = useState("");

  const [isHybrid, setIsHybrid] = useState(false);
  const [hybridTab, setHybridTab] = useState("main"); // 'main' | 'cross'
  const [selectedStringIdCross, setSelectedStringIdCross] = useState(null);
  const [prestretchEnabledCross, setPrestretchEnabledCross] = useState(false);
  const [prestretchPercentCross, setPrestretchPercentCross] = useState("");
  const [showHybridInfo, setShowHybridInfo] = useState(false);
  const hybridInfoRef = useRef(null);

  // Stock check for the currently chosen service/location
  const isStringAvailableAtLocation = (item) => {
    if (!item) return false;
    if (pickupService !== "in-store") return !!item.availableInAnyStore;
    if (instoreLocation === "Jumeriah") return item.jumeriah_stock > 0;
    if (instoreLocation === "Dubai Hills") return item.dubaihills_stock > 0;
    if (instoreLocation === "Marina") return !!item.availableInAnyStore;
    return false;
  };

  // Drop any selected string that isn't in stock at the current location
  // (e.g. picked at Jumeriah, then switched service/location)
  useEffect(() => {
    if (strings.length === 0) return;
    if (selectedStringId && !isStringAvailableAtLocation(strings.find((s) => s.string_id === selectedStringId))) {
      setSelectedStringId(null);
      setSelectedStringType("");
    }
    if (selectedStringIdCross && !isStringAvailableAtLocation(strings.find((s) => s.string_id === selectedStringIdCross))) {
      setSelectedStringIdCross(null);
    }
  }, [pickupService, instoreLocation, strings]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (hybridInfoRef.current && !hybridInfoRef.current.contains(event.target)) {
        setShowHybridInfo(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  // String tension state
  const [isTensionModalOpen, setIsTensionModalOpen] = useState(false);
  const [tensionModalTarget, setTensionModalTarget] = useState("all"); // 'all' | 'main' | 'cross'
  const [tensionOption, setTensionOption] = useState(""); // '' | 'standard' | 'custom' | 'support'
  const [tensionOptionMain, setTensionOptionMain] = useState(""); // '' | 'standard' | 'custom' | 'support'
  const [tensionOptionCross, setTensionOptionCross] = useState(""); // '' | 'standard' | 'custom' | 'support'
  const [tensionMain, setTensionMain] = useState("");
  const [tensionCross, setTensionCross] = useState("");
  const [tensionUnit, setTensionUnit] = useState("lbs");
  const [prestretchEnabled, setPrestretchEnabled] = useState(false);
  const [prestretchPercent, setPrestretchPercent] = useState("");

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await getCustomerProfile();
        if (!prevCustomerName) {
          setCustomerName(
            `${response.data.first_name} ${response.data.last_name}`
          );
        }
        if (!stateCustomerId) {
          setCustomerId(response.data.customer_id);
        }
      } catch (error) {
        console.error("Error fetching profile:", error);
      }
    };
    fetchProfile();
  }, [prevCustomerName, stateCustomerId]);

  // Fetch strings from inventory
  useEffect(() => {
    const fetchStrings = async () => {
      try {
        setLoadingStrings(true);
        const data = await getAllStringsFromInventory();
        setStrings(data);
        setApiError("");
      } catch (error) {
        console.error("Error fetching strings:", error);
        setApiError("Failed to load string inventory. Please try again.");
      } finally {
        setLoadingStrings(false);
      }
    };

    fetchStrings();
  }, []);

  const initialFreeUsed = parseInt(usedServices, 10) || 0;

  useEffect(() => {
    if (selectedStringId && selectedStringType) {
      const stringDetails = strings.find(
        (item) => item.string_id === selectedStringId
      );
      if (stringDetails) {
        if (isHybrid) {
          const stringDetailsCross = selectedStringIdCross ? strings.find(
            (item) => item.string_id === selectedStringIdCross
          ) : null;
          const isMainByos = stringDetails.name === BYOS_NAME || (stringDetails.name || "").toLowerCase().includes("bring own");
          const isCrossByos = stringDetailsCross ? (stringDetailsCross.name === BYOS_NAME || (stringDetailsCross.name || "").toLowerCase().includes("bring own")) : false;
          const bothByos = isMainByos && isCrossByos;
          const isExpress = selectedStringType === "Express";

          let cost;
          if (bothByos) {
            // Both are bring own string: 75 regular, 100 express
            if (isFree) {
              cost = isExpress ? 25 : 0;
            } else {
              cost = isExpress ? 100 : 75;
            }
          } else {
            // Consolidated hybrid pricing: 200 regular, 225 express
            if (isFree) {
              cost = isExpress ? 150 : 125;
            } else {
              cost = isExpress ? 225 : 200;
            }
          }
          setTotalCost(cost);
        } else {
          let cost;
          if (isFree) {
            cost =
              selectedStringType === "Express"
                ? Math.max(stringDetails.basePrice - 75 + 25, 0)
                : Math.max(stringDetails.basePrice - 75, 0);
          } else {
            cost =
              selectedStringType === "Express"
                ? stringDetails.basePrice + 25
                : stringDetails.basePrice;
          }
          setTotalCost(cost);
        }
      }
    } else {
      setTotalCost("");
    }
  }, [selectedStringId, selectedStringIdCross, selectedStringType, isHybrid, isFree, strings]);

  useEffect(() => {
    const fresh = Array.isArray(previousBookings) ? previousBookings : [];
    localStorage.setItem("bookings", JSON.stringify(fresh));
  }, [previousBookings]);

  const handleNextClick = () => {
    if (!bookingDate.trim()) {
      setValidationError("Please select a booking date.");
      return;
    }
    if (!serviceSpeed) {
      setValidationError(
        "Please select when you want your racket ready (Regular or Express)."
      );
      return;
    }
    if (!pickupService) {
      setValidationError("Please select a pickup/delivery service.");
      return;
    }
    if (pickupService === "in-store" && !instoreLocation) {
      setValidationError("Please select a store location.");
      return;
    }
    if (!selectedStringId) {
      setHybridTab("main");
      setValidationError("Please select a main string before proceeding.");
      return;
    }

    if (isHybrid && !selectedStringIdCross) {
      setHybridTab("cross");
      setValidationError("Please select a cross string before proceeding.");
      return;
    }

    // Validate selected string is available at chosen location
    if (pickupService === "in-store" && instoreLocation) {
      const selectedString = strings.find(s => s.string_id === selectedStringId);
      if (selectedString) {
        let stockAtLocation = 0;
        const location = instoreLocation.toLowerCase().replace(/\s+/g, "");
        
        if (location === "jumeriah" || location === "jumeirah") {
          stockAtLocation = selectedString.jumeriah_stock || 0;
        } else if (location === "dubaihills") {
          stockAtLocation = selectedString.dubaihills_stock || 0;
        } else if (location === "marina") {
          stockAtLocation = Math.max(
            selectedString.jumeriah_stock || 0,
            selectedString.dubaihills_stock || 0
          );
        }
        
        if (stockAtLocation <= 0) {
          setValidationError(
            `Selected main string "${selectedString.name}" is out of stock at ${instoreLocation}. Please select a different string or change location.`
          );
          return;
        }
      }

      if (isHybrid && selectedStringIdCross) {
        const selectedCross = strings.find(s => s.string_id === selectedStringIdCross);
        if (selectedCross) {
          let stockAtLocation = 0;
          const location = instoreLocation.toLowerCase().replace(/\s+/g, "");
          
          if (location === "jumeriah" || location === "jumeirah") {
            stockAtLocation = selectedCross.jumeriah_stock || 0;
          } else if (location === "dubaihills") {
            stockAtLocation = selectedCross.dubaihills_stock || 0;
          } else if (location === "marina") {
            stockAtLocation = Math.max(
              selectedCross.jumeriah_stock || 0,
              selectedCross.dubaihills_stock || 0
            );
          }
          
          if (stockAtLocation <= 0) {
            setValidationError(
              `Selected cross string "${selectedCross.name}" is out of stock at ${instoreLocation}. Please select a different string or change location.`
            );
            return;
          }
        }
      }
    }
    if (isHybrid) {
      if (!tensionOptionMain) {
        setValidationError("Please select a tension option for your main string.");
        return;
      }
      if (tensionOptionMain === "custom") {
        if (!tensionMain || parseFloat(tensionMain) <= 0) {
          setValidationError("Please enter a valid main tension value.");
          return;
        }
        if (prestretchEnabled && (!prestretchPercent || parseFloat(prestretchPercent) <= 0)) {
          setValidationError("Please enter a valid main prestretch percentage.");
          return;
        }
      }
      if (!tensionOptionCross) {
        setValidationError("Please select a tension option for your cross string.");
        return;
      }
      if (tensionOptionCross === "custom") {
        if (!tensionCross || parseFloat(tensionCross) <= 0) {
          setValidationError("Please enter a valid cross tension value.");
          return;
        }
        if (prestretchEnabledCross && (!prestretchPercentCross || parseFloat(prestretchPercentCross) <= 0)) {
          setValidationError("Please enter a valid cross prestretch percentage.");
          return;
        }
      }
    } else {
      if (!tensionOption) {
        setValidationError("Please select a string tension option.");
        return;
      }
      if (tensionOption === "custom") {
        if (!tensionMain || parseFloat(tensionMain) <= 0) {
          setValidationError("Please enter a valid main tension value.");
          return;
        }
        if (!tensionCross || parseFloat(tensionCross) <= 0) {
          setValidationError("Please enter a valid cross tension value.");
          return;
        }
        if (prestretchEnabled && (!prestretchPercent || parseFloat(prestretchPercent) <= 0)) {
          setValidationError("Please enter a valid main prestretch percentage.");
          return;
        }
      }
    }
    if (!isFree && !prevRacketName && !racketNameInput.trim()) {
      setValidationError("Please enter a racket name.");
      return;
    }

    setValidationError("");

    const isMainCustom = isHybrid ? tensionOptionMain === "custom" : tensionOption === "custom";
    const isCrossCustom = isHybrid ? tensionOptionCross === "custom" : tensionOption === "custom";
    const computedTensionType = !isHybrid
      ? tensionOption
      : (tensionOptionMain === "standard" && tensionOptionCross === "standard")
        ? "standard"
        : (tensionOptionMain === "support" && tensionOptionCross === "support")
          ? "support"
          : "custom";

    const newBooking = {
      customerId,
      racketName: racketNameInput,
      bookingDate,
      stringId: selectedStringId,
      stringType: serviceSpeed,
      freeService: isFree,
      rp_id,
      tensionType: computedTensionType,
      tensionOptionMain: isHybrid ? tensionOptionMain : tensionOption,
      tensionOptionCross: isHybrid ? tensionOptionCross : tensionOption,
      tensionMain: isMainCustom && tensionMain ? parseFloat(tensionMain) : null,
      tensionCross: isCrossCustom && tensionCross ? parseFloat(tensionCross) : null,
      tensionUnit: (isMainCustom || isCrossCustom) ? tensionUnit : null,
      prestretch: isMainCustom && prestretchEnabled ? 1 : 0,
      prestretchPercent:
        isMainCustom && prestretchEnabled && prestretchPercent
          ? parseFloat(prestretchPercent)
          : null,
      isHybrid: isHybrid ? 1 : 0,
      stringIdCross: isHybrid ? selectedStringIdCross : null,
      prestretchCross: isHybrid && isCrossCustom && prestretchEnabledCross ? 1 : 0,
      prestretchPercentCross:
        isHybrid && isCrossCustom && prestretchEnabledCross && prestretchPercentCross
          ? parseFloat(prestretchPercentCross)
          : null,
    };

    const updatedBookings = [...previousBookings, newBooking];
    localStorage.setItem("bookings", JSON.stringify(updatedBookings));

    navigate("/customer/dashboard/booking-final", {
      state: {
        customerId,
        customerName,
        bookingDate,
        freeServices,
        usedServices: initialFreeUsed,
        freeServicesTotal: freeServices,
        pickupService,
        services: "Stringing Service",
        string_id: selectedStringId,
        string_type: serviceSpeed,
        instoreLocation,
        rp_id,
        racketName: racketNameInput,
        isFreeService: isFree,
        previousBookings: updatedBookings,
        address: prevAddress || contextAddress,
      },
    });
  };

  const BYOS_NAME = "Bring Own String";

  const scrollToBYOS = () => {
    const byosString = strings.find((s) => s.name === BYOS_NAME || (s.name || "").toLowerCase().includes("bring own"));

    if (byosString) {
      if (isHybrid && hybridTab === "cross") {
        setSelectedStringIdCross(byosString.string_id);
      } else {
        setSelectedStringId(byosString.string_id);
        setSelectedStringType(serviceSpeed || "Regular");
        if (isHybrid && !selectedStringIdCross) {
          setHybridTab("cross");
        }
      }
      if (!isHybrid) {
        setIsTensionModalOpen(true);
      }
    }

    const targetId = isHybrid && hybridTab === "cross" ? "byos-row-cross" : "byos-row";
    const target = document.getElementById(targetId) || document.getElementById("byos-row");
    if (target) {
      target.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const renderStringCards = (isCross = false) => {
    const sortedStrings = strings.slice().sort((a, b) => {
      let aAvailable = false;
      let bAvailable = false;

      if (pickupService === "in-store") {
        if (instoreLocation === "Jumeriah") {
          aAvailable = a.jumeriah_stock > 0;
          bAvailable = b.jumeriah_stock > 0;
        } else if (instoreLocation === "Dubai Hills") {
          aAvailable = a.dubaihills_stock > 0;
          bAvailable = b.dubaihills_stock > 0;
        } else if (instoreLocation === "Marina") {
          aAvailable = a.availableInAnyStore;
          bAvailable = b.availableInAnyStore;
        }
      } else {
        aAvailable = a.availableInAnyStore;
        bAvailable = b.availableInAnyStore;
      }

      if (aAvailable !== bAvailable) return aAvailable ? -1 : 1;
      return (a.name || "").localeCompare(b.name || "");
    });

    if (sortedStrings.length === 0) {
      return <p className={styles.noStrings}>No strings in inventory.</p>;
    }

    return sortedStrings.map((stringItem) => {
      const originalPrice =
        serviceSpeed === "Express"
          ? stringItem.basePrice + 25
          : stringItem.basePrice;

      const priceForRow = isFree ? Math.max(originalPrice - 75, 0) : originalPrice;

      const isSelected = isCross
        ? selectedStringIdCross === stringItem.string_id
        : selectedStringId === stringItem.string_id;
      const isBYOS = stringItem.name === BYOS_NAME || (stringItem.name || "").toLowerCase().includes("bring own");

      let isAvailableAtSelectedLocation = false;
      let availableAtOtherStore = null;
      let isOutOfStockEverywhere = false;

      if (pickupService === "in-store") {
        if (instoreLocation === "Jumeriah") {
          isAvailableAtSelectedLocation = stringItem.jumeriah_stock > 0;
          if (!isAvailableAtSelectedLocation && stringItem.dubaihills_stock > 0) {
            availableAtOtherStore = "Dubai Hills";
          }
        } else if (instoreLocation === "Dubai Hills") {
          isAvailableAtSelectedLocation = stringItem.dubaihills_stock > 0;
          if (!isAvailableAtSelectedLocation && stringItem.jumeriah_stock > 0) {
            availableAtOtherStore = "Jumeriah";
          }
        } else if (instoreLocation === "Marina") {
          isAvailableAtSelectedLocation = stringItem.availableInAnyStore;
        }
      } else {
        isAvailableAtSelectedLocation = stringItem.availableInAnyStore;
      }

      isOutOfStockEverywhere = !stringItem.jumeriah_stock && !stringItem.dubaihills_stock;

      const cdnBase = process.env.REACT_APP_CDN_URL || "https://cdn11.bigcommerce.com/s-meuoh09dlj";
      const imageUrl = stringItem.image_url
        ? (stringItem.image_url.startsWith("http") ? stringItem.image_url : `${cdnBase}${stringItem.image_url}`)
        : "/images/strings/default.jpg";

      return (
        <div
          key={stringItem.string_id}
          className={`${styles.card} ${isSelected ? styles.cardSelected : ""} ${
            !isAvailableAtSelectedLocation ? styles.cardDisabled : ""
          }`}
          id={isBYOS ? (isCross ? "byos-row-cross" : "byos-row") : undefined}
        >
          <label className={styles.cardTop}>
            <input
              type="radio"
              name={isCross ? "selectedStringCross" : "selectedStringMain"}
              value={stringItem.string_id || ""}
              onChange={() => {
                if (!isAvailableAtSelectedLocation) return;
                if (isCross) {
                  setSelectedStringIdCross(stringItem.string_id);
                  if (isHybrid) {
                    setTensionModalTarget("cross");
                  } else {
                    setTensionModalTarget("all");
                  }
                  setIsTensionModalOpen(true);
                } else {
                  setSelectedStringId(stringItem.string_id);
                  setSelectedStringType(serviceSpeed);
                  if (isHybrid) {
                    setTensionModalTarget("main");
                  } else {
                    setTensionModalTarget("all");
                  }
                  setIsTensionModalOpen(true);
                  if (isHybrid && !selectedStringIdCross) {
                    setHybridTab("cross");
                  }
                }
              }}
              checked={isSelected}
              disabled={!isAvailableAtSelectedLocation}
              className={styles.cardSelector}
              aria-label={`Select ${stringItem.name}`}
            />

            <div className={styles.imageWrap}>
              <img
                className={styles.cardImage}
                src={imageUrl}
                alt={stringItem.name}
                loading="lazy"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/images/strings/default.jpg";
                }}
              />
              <div className={styles.imageBadges}>
                {isOutOfStockEverywhere && <span className={styles.oosBadge}>Out of Stock</span>}
                {!isOutOfStockEverywhere && !isAvailableAtSelectedLocation && availableAtOtherStore && (
                  <span className={styles.switchStoreBadge}>Available at {availableAtOtherStore}</span>
                )}
              </div>
            </div>
          </label>

          <div className={styles.cardBody}>
            <div className={styles.cardTitle} title={`${stringItem.name} (${stringItem.brand})`}>
              {stringItem.name} ({stringItem.brand})
            </div>

            {!isOutOfStockEverywhere && !isAvailableAtSelectedLocation && availableAtOtherStore && (
              <div className={styles.switchStoreMessage}>
                Want to switch to {availableAtOtherStore} store?
              </div>
            )}

            <div className={styles.cardInfo}>
              {isHybrid ? (
                <span className={styles.badge}>{isBYOS ? "Own String" : "Half String (Hybrid)"}</span>
              ) : (
                <>
                  <span className={styles.badge}>{serviceSpeed}</span>
                  <span className={styles.cardPrice}>
                    {isFree ? (
                      <>
                        <span style={{ textDecoration: "line-through", marginRight: "8px", opacity: 0.6 }}>
                          AED {originalPrice}
                        </span>
                        AED {priceForRow}
                      </>
                    ) : (
                      <>
                        AED {priceForRow}
                      </>
                    )}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>
      );
    });
  };

  const effectiveConstraint = stringTypeConstraint;

  return (
    <div className={styles.bookingForm}>
      <h2 className={styles.title}>Create Booking</h2>

      <div className={styles.detailsContainer}>
        {validationError && (
          <p className={styles.validationError} role="alert">
            {validationError}
          </p>
        )}
        <div className={styles.detailRow}>
          <div className={styles.detailItem}>
            <label className={styles.label}>Customer Name:</label>
            <span className={styles.disabledText}>{customerName}</span>
          </div>
          <div className={styles.detailItem}>
            <label className={styles.label}>Services:</label>
            <span className={styles.disabledText}>Stringing Service</span>
          </div>
        </div>

        <div className={styles.detailRow}>
          <div className={styles.detailItem}>
            <label className={styles.label} htmlFor="bf-date">
              Booking Date:
            </label>
            <input
              id="bf-date"
              type="date"
              value={bookingDate}
              onChange={(e) => setBookingDate(e.target.value)}
              className={`${styles.dateInput} ${
                effectiveConstraint ? styles.dateInputDisabled : ""
              }`}
              min={getCurrentDate()}
              disabled={effectiveConstraint}
            />
          </div>
        </div>

        <div className={styles.formGroup}>
          <label className={styles.label} htmlFor="bf-racket">
            Racket Name:
          </label>
          <input
            id="bf-racket"
            type="text"
            className={styles.inputField}
            placeholder="Enter racket name"
            value={racketNameInput}
            onChange={(e) => setRacketNameInput(e.target.value)}
            disabled={isFree || !!rp_id}
          />
        </div>

        {/* Service Speed Selection */}
        <div className={styles.formGroup}>
          <label className={styles.label}>
            When will your racket be ready?
          </label>
          <div className={styles.serviceSpeedOptions}>
            <label
              className={`${styles.serviceSpeedOption} ${
                effectiveConstraint && effectiveConstraint !== "Regular"
                  ? styles.serviceSpeedOptionDisabled
                  : ""
              }`}
            >
              <input
                type="radio"
                name="serviceSpeed"
                value="Regular"
                checked={serviceSpeed === "Regular"}
                onChange={(e) => {
                  setServiceSpeed(e.target.value);
                  setSelectedStringType("Regular");
                  setPickupService(""); // Reset pickup service when speed changes
                }}
                disabled={
                  effectiveConstraint && effectiveConstraint !== "Regular"
                }
              />
              <div className={styles.serviceSpeedContent}>
                <strong>Regular: Next day</strong>
                <span className={styles.serviceSpeedDescription}>
                  String installation ready by next day
                </span>
              </div>
            </label>
            <label
              className={`${styles.serviceSpeedOption} ${
                (effectiveConstraint && effectiveConstraint !== "Express") ||
                isExpressDisabledForForm
                  ? styles.serviceSpeedOptionDisabled
                  : ""
              }`}
            >
              <input
                type="radio"
                name="serviceSpeed"
                value="Express"
                checked={serviceSpeed === "Express"}
                onChange={(e) => {
                  setServiceSpeed(e.target.value);
                  setSelectedStringType("Express");
                  setPickupService(""); // Reset pickup service when speed changes
                }}
                disabled={
                  (effectiveConstraint && effectiveConstraint !== "Express") ||
                  isExpressDisabledForForm
                }
              />
              <div className={styles.serviceSpeedContent}>
                <strong>Express: Within 6 hours</strong>
                <span className={styles.serviceSpeedDescription}>
                  {isExpressDisabledForForm
                    ? getExpressDisabledReason()
                    : "Fast-track service for urgent needs"}
                </span>
              </div>
            </label>
          </div>
        </div>

        {/* Pickup/Delivery Service Selection */}
        {serviceSpeed && (
          <div className={styles.formGroup}>
            <label className={styles.label}>
              Select Pickup/Delivery Service:
            </label>
            <div className={styles.pickupServiceOptions}>
              <label
                className={`${styles.pickupOption} ${
                  lockServiceSelection && pickupService !== "in-store"
                    ? styles.pickupOptionDisabled
                    : ""
                }`}
              >
                <input
                  type="radio"
                  name="pickupService"
                  value="in-store"
                  checked={pickupService === "in-store"}
                  onChange={(e) => setPickupService(e.target.value)}
                  disabled={lockServiceSelection}
                />
                <span>In-Store (No Charge)</span>
              </label>
              {serviceSpeed === "Regular" ? (
                <>
                  <label
                    className={`${styles.pickupOption} ${
                      lockServiceSelection && pickupService !== "pickup"
                        ? styles.pickupOptionDisabled
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="pickupService"
                      value="pickup"
                      checked={pickupService === "pickup"}
                      onChange={(e) => setPickupService(e.target.value)}
                      disabled={lockServiceSelection}
                    />
                    <span>
                      Pickup Only (+{" "}
                      <span className="dirham-symbol">&#xea;</span> 30)
                    </span>
                  </label>
                  <label
                    className={`${styles.pickupOption} ${
                      lockServiceSelection && pickupService !== "delivery"
                        ? styles.pickupOptionDisabled
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="pickupService"
                      value="delivery"
                      checked={pickupService === "delivery"}
                      onChange={(e) => setPickupService(e.target.value)}
                      disabled={lockServiceSelection}
                    />
                    <span>
                      Delivery Only (+{" "}
                      <span className="dirham-symbol">&#xea;</span> 30)
                    </span>
                  </label>
                  <label
                    className={`${styles.pickupOption} ${
                      lockServiceSelection &&
                      pickupService !== "pickup/delivery"
                        ? styles.pickupOptionDisabled
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="pickupService"
                      value="pickup/delivery"
                      checked={pickupService === "pickup/delivery"}
                      onChange={(e) => setPickupService(e.target.value)}
                      disabled={lockServiceSelection}
                    />
                    <span>
                      Both Pickup & Delivery (+{" "}
                      <span className="dirham-symbol">&#xea;</span> 60)
                    </span>
                  </label>
                </>
              ) : (
                <>
                  <label
                    className={`${styles.pickupOption} ${
                      lockServiceSelection && pickupService !== "express-pickup"
                        ? styles.pickupOptionDisabled
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="pickupService"
                      value="express-pickup"
                      checked={pickupService === "express-pickup"}
                      onChange={(e) => setPickupService(e.target.value)}
                      disabled={lockServiceSelection}
                    />
                    <span>
                      Express Pickup (+{" "}
                      <span className="dirham-symbol">&#xea;</span> 50)
                    </span>
                  </label>
                  <label
                    className={`${styles.pickupOption} ${
                      lockServiceSelection &&
                      pickupService !== "express-delivery"
                        ? styles.pickupOptionDisabled
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="pickupService"
                      value="express-delivery"
                      checked={pickupService === "express-delivery"}
                      onChange={(e) => setPickupService(e.target.value)}
                      disabled={lockServiceSelection}
                    />
                    <span>
                      Express Delivery (+{" "}
                      <span className="dirham-symbol">&#xea;</span> 50)
                    </span>
                  </label>
                  <label
                    className={`${styles.pickupOption} ${
                      lockServiceSelection &&
                      pickupService !== "express-pickup/delivery"
                        ? styles.pickupOptionDisabled
                        : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="pickupService"
                      value="express-pickup/delivery"
                      checked={pickupService === "express-pickup/delivery"}
                      onChange={(e) => setPickupService(e.target.value)}
                      disabled={lockServiceSelection}
                    />
                    <span>
                      Express Pickup & Delivery (+{" "}
                      <span className="dirham-symbol">&#xea;</span> 100)
                    </span>
                  </label>
                </>
              )}
            </div>
          </div>
        )}

        {/* Store Location Selection for In-Store */}
        {pickupService === "in-store" && (
          <div className={styles.formGroup}>
            <label className={styles.label}>Select Store Location:</label>
            <select
              className={styles.inputField}
              value={instoreLocation}
              onChange={(e) => {
                const newLocation = e.target.value;
                setInstoreLocation(newLocation);
                
                // Clear selected strings when location changes
                // User must re-select strings for the new location
                setSelectedStringId(null);
                setSelectedStringIdCross(null);
                setSelectedStringType("");
                setTensionOption("");
                setTensionOptionMain("");
                setTensionOptionCross("");
                setTensionMain("");
                setTensionCross("");
                setPrestretchEnabled(false);
                setPrestretchPercent("");
                setPrestretchEnabledCross(false);
                setPrestretchPercentCross("");
                setHybridTab("main");
              }}
              disabled={lockServiceSelection}
            >
              <option value="">-- Select Location --</option>
              <option value="Jumeriah">Jumeriah</option>
              <option value="Dubai Hills">Dubai Hills</option>
              <option value="Marina">Marina (Any Available Location)</option>
            </select>
            {instoreLocation === "Marina" && !expressSettings.marina_express_enabled && (
              <span style={{ fontSize: "0.825rem", color: "#c2410c", marginTop: "6px", display: "block" }}>
                ℹ️ Note: Express service is not available at the Marina location (Regular service only).
              </span>
            )}
            {instoreLocation === "Dubai Hills" && !expressSettings.dubaihills_express_enabled && (
              <span style={{ fontSize: "0.825rem", color: "#c2410c", marginTop: "6px", display: "block" }}>
                ℹ️ Note: Express service is not available at the Dubai Hills location (Regular service only).
              </span>
            )}
          </div>
        )}
      </div>

      {pickupService && (pickupService === "in-store" ? instoreLocation : true) && (
        <div className={styles.formGroup} style={{ margin: "20px 24px" }}>
          <label className={styles.label}>Stringing Option:</label>
          <div className={styles.radioGroup}>
            <label className={styles.pickupOption}>
              <input
                type="radio"
                name="hybridOption"
                checked={!isHybrid}
                onChange={() => {
                  setIsHybrid(false);
                  setSelectedStringIdCross(null);
                  setPrestretchEnabledCross(false);
                  setPrestretchPercentCross("");
                  setHybridTab("main");
                }}
              />
              <span>Single String Setup</span>
            </label>
            <label className={styles.pickupOption}>
              <input
                type="radio"
                name="hybridOption"
                checked={isHybrid}
                onChange={() => {
                  setIsHybrid(true);
                  setHybridTab("main");
                }}
              />
              <span className={styles.hybridOptionLabel}>
                <span>Hybrid String Setup (Main & Cross)</span>
                <span
                  ref={hybridInfoRef}
                  className={styles.infoWrap}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <button
                    type="button"
                    className={styles.infoBtn}
                    aria-label="Hybrid pricing information"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setShowHybridInfo((prev) => !prev);
                    }}
                  >
                    i
                  </button>
                  <div
                    className={`${styles.infoTooltip} ${
                      showHybridInfo ? styles.infoTooltipVisible : ""
                    }`}
                    role="tooltip"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {isFree ? (
                      <>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", fontWeight: "700", color: "#60a5fa", fontSize: "0.95rem" }}>
                          <span>🎁</span> Free String Service Applied
                        </div>
                        <p style={{ margin: "0 0 6px 0", color: "#f3f4f6" }}>
                          For Hybrid Stringing, your <strong>75 AED</strong> service fee is covered!
                        </p>
                        <p style={{ margin: "0 0 6px 0", color: "#e5e7eb" }}>
                          Total cost is <strong>125 AED</strong> (200 - 75 AED) Regular / <strong>150 AED</strong> (225 - 75 AED) Express when selecting store strings.
                        </p>
                        <p style={{ margin: 0, fontSize: "0.82rem", color: "#9ca3af" }}>
                          (If you bring your own strings for both main and cross: <strong>0 AED</strong> Regular / <strong>25 AED</strong> Express).
                        </p>
                      </>
                    ) : (
                      <>
                        <div style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "6px", fontWeight: "700", color: "var(--primary-color, #d2fa10)", fontSize: "0.95rem" }}>
                          <span>ℹ️</span> Consolidated Hybrid Pricing
                        </div>
                        <p style={{ margin: "0 0 6px 0", color: "#f3f4f6" }}>
                          Flat <strong>200 AED</strong> Regular / <strong>225 AED</strong> Express when selecting store strings (or <strong>75 AED</strong> Regular / <strong>100 AED</strong> Express if bringing your own strings for both).
                        </p>
                        <p style={{ margin: 0, fontSize: "0.82rem", color: "#9ca3af" }}>
                          *With Free String Service: 75 AED service fee is deducted &mdash; <strong>125 AED</strong> (200 - 75) Regular / <strong>150 AED</strong> (225 - 75) Express.
                        </p>
                      </>
                    )}
                  </div>
                </span>
              </span>
            </label>
          </div>
        </div>
      )}

      {/* String Selection - Only show when service is selected */}
      {pickupService &&
        (pickupService === "in-store" ? instoreLocation : true) && (
          <div className={styles.formGroup}>
            {isHybrid ? (
              <>
                {/* --- TWO TABS FOR HYBRID SELECTION --- */}
                {(() => {
                  const selectedMain = strings.find((s) => s.string_id === selectedStringId);
                  const selectedCross = strings.find((s) => s.string_id === selectedStringIdCross);
                  return (
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "12px",
                      marginBottom: "20px",
                      marginTop: "10px"
                    }}>
                      <button
                        type="button"
                        onClick={() => setHybridTab("main")}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          padding: "14px 18px",
                          borderRadius: "10px",
                          border: hybridTab === "main" ? "2px solid #0b7b43" : "1px solid #d1d5db",
                          background: hybridTab === "main" ? "#f0fdf4" : "#ffffff",
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.2s ease",
                          boxShadow: hybridTab === "main" ? "0 2px 8px rgba(11, 123, 67, 0.15)" : "none"
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", marginBottom: "4px" }}>
                          <span style={{ fontWeight: "700", fontSize: "1.05rem", color: hybridTab === "main" ? "#0b7b43" : "#374151" }}>
                            1. Main String
                          </span>
                          {selectedMain ? (
                            <span style={{ background: "#0b7b43", color: "#fff", fontSize: "0.75rem", padding: "2px 8px", borderRadius: "12px", fontWeight: "600" }}>
                              ✓ Selected
                            </span>
                          ) : (
                            <span style={{ background: "#e5e7eb", color: "#6b7280", fontSize: "0.75rem", padding: "2px 8px", borderRadius: "12px", fontWeight: "500" }}>
                              Required
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: "0.9rem", color: selectedMain ? "#111827" : "#9ca3af", fontWeight: selectedMain ? "600" : "400" }}>
                          {selectedMain ? `${selectedMain.brand} ${selectedMain.name}` : "Click to select main string"}
                        </span>
                        {tensionOptionMain ? (
                          <div style={{ marginTop: "6px", fontSize: "0.8rem", color: "#0b7b43", fontWeight: "600", display: "flex", alignItems: "center", gap: "6px" }}>
                            <span>
                              Tension: {tensionOptionMain === "custom" ? (tensionMain ? `${tensionMain} ${tensionUnit}` : "Custom") : tensionOptionMain === "standard" ? "Standard" : "Talk with support"}
                            </span>
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                setTensionModalTarget("main");
                                setIsTensionModalOpen(true);
                              }}
                              style={{ textDecoration: "underline", cursor: "pointer", color: "#0b7b43", fontSize: "0.75rem" }}
                            >
                              (Edit)
                            </span>
                          </div>
                        ) : null}
                      </button>

                      <button
                        type="button"
                        onClick={() => setHybridTab("cross")}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          padding: "14px 18px",
                          borderRadius: "10px",
                          border: hybridTab === "cross" ? "2px solid #0b7b43" : "1px solid #d1d5db",
                          background: hybridTab === "cross" ? "#f0fdf4" : "#ffffff",
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.2s ease",
                          boxShadow: hybridTab === "cross" ? "0 2px 8px rgba(11, 123, 67, 0.15)" : "none"
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%", marginBottom: "4px" }}>
                          <span style={{ fontWeight: "700", fontSize: "1.05rem", color: hybridTab === "cross" ? "#0b7b43" : "#374151" }}>
                            2. Cross String
                          </span>
                          {selectedCross ? (
                            <span style={{ background: "#0b7b43", color: "#fff", fontSize: "0.75rem", padding: "2px 8px", borderRadius: "12px", fontWeight: "600" }}>
                              ✓ Selected
                            </span>
                          ) : (
                            <span style={{ background: "#fed7aa", color: "#c2410c", fontSize: "0.75rem", padding: "2px 8px", borderRadius: "12px", fontWeight: "600" }}>
                              Action Needed
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: "0.9rem", color: selectedCross ? "#111827" : "#9ca3af", fontWeight: selectedCross ? "600" : "400" }}>
                          {selectedCross ? `${selectedCross.brand} ${selectedCross.name}` : "Click to select cross string"}
                        </span>
                        {tensionOptionCross ? (
                          <div style={{ marginTop: "6px", fontSize: "0.8rem", color: "#0b7b43", fontWeight: "600", display: "flex", alignItems: "center", gap: "6px" }}>
                            <span>
                              Tension: {tensionOptionCross === "custom" ? (tensionCross ? `${tensionCross} ${tensionUnit}` : "Custom") : tensionOptionCross === "standard" ? "Standard" : "Talk with support"}
                            </span>
                            <span
                              role="button"
                              tabIndex={0}
                              onClick={(e) => {
                                e.stopPropagation();
                                setTensionModalTarget("cross");
                                setIsTensionModalOpen(true);
                              }}
                              style={{ textDecoration: "underline", cursor: "pointer", color: "#0b7b43", fontSize: "0.75rem" }}
                            >
                              (Edit)
                            </span>
                          </div>
                        ) : null}
                      </button>
                    </div>
                  );
                })()}

                {/* --- ACTIVE TAB HEADING --- */}
                <div style={{ marginBottom: "20px" }}>
                  <div className={styles.labelRow}>
                    <label className={styles.label} style={{ fontSize: "1.2rem", color: "#0b7b43" }}>
                      {hybridTab === "main" ? "1. Select Main String" : "2. Select Cross String"}
                      {pickupService === "in-store" && instoreLocation && (
                        <span className={styles.locationBadge}>
                          {" "}
                          (Available at {instoreLocation})
                        </span>
                      )}
                    </label>
                    <button
                      type="button"
                      className={styles.byosInlineBtn}
                      onClick={scrollToBYOS}
                    >
                      Want to bring your own strings? Click here
                    </button>
                  </div>
                  
                  {loadingStrings && <p>Loading string inventory...</p>}
                  {apiError && <p className={styles.validationError}>{apiError}</p>}
                  
                  {!loadingStrings && !apiError && (
                    <div className={styles.cardsGrid}>
                      {renderStringCards(hybridTab === "cross")}
                    </div>
                  )}

                  {/* Switch to Cross tab helper if main is selected */}
                  {hybridTab === "main" && selectedStringId && !selectedStringIdCross && (
                    <div style={{ textAlign: "center", marginTop: "24px" }}>
                      <button
                        type="button"
                        onClick={() => setHybridTab("cross")}
                        style={{
                          background: "#0b7b43",
                          color: "#fff",
                          border: "none",
                          borderRadius: "8px",
                          padding: "12px 26px",
                          fontSize: "1rem",
                          fontWeight: "600",
                          cursor: "pointer",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.12)"
                        }}
                      >
                        Next: Choose Cross String &rarr;
                      </button>
                    </div>
                  )}
                </div>
              </>
            ) : (
              <>
                <div className={styles.labelRow}>
                  <label className={styles.label}>
                    Select Racket String
                    {pickupService === "in-store" && instoreLocation && (
                      <span className={styles.locationBadge}>
                        {" "}
                        (Available at {instoreLocation})
                      </span>
                    )}
                  </label>
                  <button
                    type="button"
                    className={styles.byosInlineBtn}
                    onClick={scrollToBYOS}
                  >
                    Want to bring your own strings? Click here
                  </button>
                </div>

                {loadingStrings && <p>Loading string inventory...</p>}
                {apiError && <p className={styles.validationError}>{apiError}</p>}

                {!loadingStrings && !apiError && (
                  <div className={styles.cardsGrid}>
                    {renderStringCards(false)}
                  </div>
                )}
              </>
            )}
          </div>
        )}

      {/* Show buttons only when string is selected */}
      {selectedStringId && (
        <button
          type="button"
          onClick={() => {
            if (isHybrid) {
              if (!selectedStringIdCross) {
                setTensionModalTarget("main");
              } else {
                setTensionModalTarget("all");
              }
            } else {
              setTensionModalTarget("all");
            }
            setIsTensionModalOpen(true);
          }}
          className={styles.editTensionBtn}
        >
          <span>
            {isHybrid ? (
              (tensionOptionMain || tensionOptionCross) ? (
                `Tension: Main (${tensionOptionMain === "custom" ? (tensionMain ? `${tensionMain} ${tensionUnit}` : "Custom") : tensionOptionMain === "standard" ? "Standard" : tensionOptionMain === "support" ? "Support" : "Not set"}) / Cross (${tensionOptionCross === "custom" ? (tensionCross ? `${tensionCross} ${tensionUnit}` : "Custom") : tensionOptionCross === "standard" ? "Standard" : tensionOptionCross === "support" ? "Support" : "Not set"})`
              ) : "Select String Tension (Required)"
            ) : (
              tensionOption === "custom"
                ? `Tension: Custom (${tensionMain || '-'} ${tensionUnit} / ${tensionCross || '-'} ${tensionUnit})`
                : tensionOption === "standard"
                ? "Tension: Standard"
                : tensionOption === "support"
                ? "Tension: Talk with Support"
                : "Select String Tension (Required)"
            )}
          </span>
          <span style={{ fontSize: "1.2rem", lineHeight: "1" }}>&#x2192;</span>
        </button>
      )}

      {selectedStringId && (!isHybrid || selectedStringIdCross) && (
        <div style={{
          background: "#f8fafc",
          border: "1px solid #e2e8f0",
          borderRadius: "10px",
          padding: "12px 16px",
          marginBottom: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "10px"
        }}>
          <div>
            <div style={{ fontWeight: "700", color: "#0f172a", fontSize: "0.95rem" }}>
              {isHybrid ? (
                <>Hybrid: {strings.find(s => s.string_id === selectedStringId)?.name || "Main"} + {strings.find(s => s.string_id === selectedStringIdCross)?.name || "Cross"}</>
              ) : (
                <>{strings.find(s => s.string_id === selectedStringId)?.name}</>
              )}
            </div>
            <div style={{ fontSize: "0.85rem", color: "#64748b" }}>
              Speed: <strong>{serviceSpeed}</strong> {isFree ? " • Free String Service Applied" : ""}
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: "0.8rem", color: "#64748b" }}>Total Cost</div>
            <div style={{ fontSize: "1.15rem", fontWeight: "800", color: "#16a34a" }}>
              {isFree && (
                <span style={{ textDecoration: "line-through", color: "#94a3b8", fontSize: "0.9rem", marginRight: "6px" }}>
                  AED {isHybrid ? (serviceSpeed === "Express" ? 225 : 200) : (serviceSpeed === "Express" ? (strings.find(s => s.string_id === selectedStringId)?.basePrice + 25 || 0) : (strings.find(s => s.string_id === selectedStringId)?.basePrice || 0))}
                </span>
              )}
              AED {_totalCost}
            </div>
          </div>
        </div>
      )}

      {selectedStringId && (
        <div className={styles.buttonContainer}>
          <div className={styles.buttonInner}>
            <button
              type="button"
              className={styles.cancelButton}
              onClick={() => {
                setSelectedStringId(null);
                setSelectedStringIdCross(null);
                setSelectedStringType("");
                setTotalCost("");
                setTensionOption("");
                setTensionOptionMain("");
                setTensionOptionCross("");
                setTensionMain("");
                setTensionCross("");
                setTensionUnit("lbs");
                setPrestretchEnabled(false);
                setPrestretchPercent("");
                setPrestretchEnabledCross(false);
                setPrestretchPercentCross("");
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.nextButton}
              onClick={handleNextClick}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {isTensionModalOpen && (
        <div className={styles.modal} onClick={() => setIsTensionModalOpen(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {isHybrid
                  ? tensionModalTarget === "main"
                    ? "Configure Main String Tension"
                    : tensionModalTarget === "cross"
                    ? "Configure Cross String Tension"
                    : "Configure String Tension (Hybrid)"
                  : "Configure String Tension"}
              </h3>
              <button 
                className={styles.modalCloseIcon} 
                onClick={() => setIsTensionModalOpen(false)}
                aria-label="Close"
              >
                &#10005;
              </button>
            </div>
            {isHybrid && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "8px",
                marginBottom: "16px",
                background: "#f3f4f6",
                padding: "4px",
                borderRadius: "10px"
              }}>
                <button
                  type="button"
                  style={{
                    padding: "10px 12px",
                    borderRadius: "8px",
                    fontWeight: "700",
                    fontSize: "0.85rem",
                    cursor: "pointer",
                    border: tensionModalTarget !== "cross" ? "2px solid #0b7b43" : "1px solid #d1d5db",
                    background: tensionModalTarget !== "cross" ? "#f0fdf4" : "#ffffff",
                    color: tensionModalTarget !== "cross" ? "#0b7b43" : "#4b5563",
                    transition: "all 0.2s ease"
                  }}
                  onClick={() => setTensionModalTarget("main")}
                >
                  1. Main String {tensionOptionMain ? `(${tensionOptionMain === "custom" ? (tensionMain ? `${tensionMain}${tensionUnit}` : "Custom") : tensionOptionMain === "standard" ? "Standard" : "Support"})` : ""}
                </button>
                <button
                  type="button"
                  style={{
                    padding: "10px 12px",
                    borderRadius: "8px",
                    fontWeight: "700",
                    fontSize: "0.85rem",
                    cursor: "pointer",
                    border: tensionModalTarget === "cross" ? "2px solid #0b7b43" : "1px solid #d1d5db",
                    background: tensionModalTarget === "cross" ? "#f0fdf4" : "#ffffff",
                    color: tensionModalTarget === "cross" ? "#0b7b43" : "#4b5563",
                    transition: "all 0.2s ease"
                  }}
                  onClick={() => setTensionModalTarget("cross")}
                >
                  2. Cross String {tensionOptionCross ? `(${tensionOptionCross === "custom" ? (tensionCross ? `${tensionCross}${tensionUnit}` : "Custom") : tensionOptionCross === "standard" ? "Standard" : "Support"})` : ""}
                </button>
              </div>
            )}
            
            {(() => {
              const activeTensionOption = isHybrid
                ? (tensionModalTarget === "cross" ? tensionOptionCross : tensionOptionMain)
                : tensionOption;

              const handleSelectTensionOption = (opt) => {
                if (isHybrid) {
                  if (tensionModalTarget === "cross") {
                    setTensionOptionCross(opt);
                  } else {
                    setTensionOptionMain(opt);
                  }
                } else {
                  setTensionOption(opt);
                }
              };

              return (
                <>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Select Option {isHybrid ? `(${tensionModalTarget === "cross" ? "Cross String" : "Main String"})` : ""}
                    </label>
                    <div className={styles.tensionOptions}>
                      <button
                        type="button"
                        className={`${styles.tensionBtn} ${
                          activeTensionOption === "standard" ? styles.tensionBtnActive : ""
                        }`}
                        onClick={() => handleSelectTensionOption("standard")}
                      >
                        Standard
                      </button>
                      <button
                        type="button"
                        className={`${styles.tensionBtn} ${
                          activeTensionOption === "custom" ? styles.tensionBtnActive : ""
                        }`}
                        onClick={() => handleSelectTensionOption("custom")}
                      >
                        Custom
                      </button>
                      <button
                        type="button"
                        className={`${styles.tensionBtn} ${
                          activeTensionOption === "support" ? styles.tensionBtnActive : ""
                        }`}
                        onClick={() => handleSelectTensionOption("support")}
                      >
                        Talk with support
                      </button>
                    </div>
                  </div>



                  {activeTensionOption === "support" && (
                    <div className={styles.tensionSupport} style={{ marginTop: "16px" }}>
                      <p>
                        Our support team will call you to discuss the right tension {isHybrid ? `for ${tensionModalTarget === "cross" ? "Cross" : "Main"} String` : ""}.{" "}
                        <a href="tel:+971503469515" className={styles.tensionSupportLink}>
                          +971 503 469 515
                        </a>
                      </p>
                    </div>
                  )}

                  {activeTensionOption === "custom" && (
                    <div className={styles.tensionCustom}>
                      <div className={styles.unitToggle}>
                        <button
                          type="button"
                          className={`${styles.unitBtn} ${
                            tensionUnit === "lbs" ? styles.unitBtnActive : ""
                          }`}
                          onClick={() => setTensionUnit("lbs")}
                        >
                          lbs
                        </button>
                        <button
                          type="button"
                          className={`${styles.unitBtn} ${
                            tensionUnit === "kg" ? styles.unitBtnActive : ""
                          }`}
                          onClick={() => setTensionUnit("kg")}
                        >
                          kg
                        </button>
                      </div>

                      {isHybrid && tensionModalTarget === "cross" && (
                        <div style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          padding: "8px 12px",
                          fontSize: "0.85rem",
                          color: "#475569",
                          marginBottom: "12px"
                        }}>
                          Main String Tension: <strong>{tensionOptionMain === "custom" ? `${tensionMain || "-"} ${tensionUnit}` : tensionOptionMain === "standard" ? "Standard" : tensionOptionMain === "support" ? "Support" : "Not configured yet"}</strong>
                        </div>
                      )}
                      {isHybrid && tensionModalTarget !== "cross" && (
                        <div style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          padding: "8px 12px",
                          fontSize: "0.85rem",
                          color: "#475569",
                          marginBottom: "12px"
                        }}>
                          Cross String Tension: <strong>{tensionOptionCross === "custom" ? `${tensionCross || "-"} ${tensionUnit}` : tensionOptionCross === "standard" ? "Standard" : tensionOptionCross === "support" ? "Support" : "Not configured yet"}</strong>
                        </div>
                      )}

                      <div className={styles.tensionFields}>
                        {(!isHybrid || tensionModalTarget !== "cross") ? (
                          <div className={styles.tensionField}>
                            <label className={styles.tensionFieldLabel}>
                              {isHybrid ? "Main String Tension" : "Main Tension"}
                            </label>
                            <div className={styles.tensionInputRow}>
                              <input
                                type="number"
                                min="1"
                                step="0.5"
                                value={tensionMain}
                                onChange={(e) => setTensionMain(e.target.value)}
                                className={styles.tensionInput}
                                placeholder="e.g. 55"
                              />
                              <span className={styles.tensionUnitLabel}>{tensionUnit}</span>
                            </div>
                          </div>
                        ) : (
                          <div className={styles.tensionField}>
                            <label className={styles.tensionFieldLabel}>
                              Cross String Tension
                            </label>
                            <div className={styles.tensionInputRow}>
                              <input
                                type="number"
                                min="1"
                                step="0.5"
                                value={tensionCross}
                                onChange={(e) => setTensionCross(e.target.value)}
                                className={styles.tensionInput}
                                placeholder="e.g. 53"
                              />
                              <span className={styles.tensionUnitLabel}>{tensionUnit}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {isHybrid ? (
                        tensionModalTarget !== "cross" ? (
                          <div className={styles.prestretchRow}>
                            <label
                              className={`${styles.prestretchLabel} ${
                                !prestretchEnabled ? styles.prestretchLabelDisabled : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={prestretchEnabled}
                                onChange={(e) => {
                                  setPrestretchEnabled(e.target.checked);
                                  if (!e.target.checked) setPrestretchPercent("");
                                }}
                                className={styles.prestretchCheckbox}
                              />
                              Main Prestretch
                            </label>
                            <div className={styles.tensionInputRow}>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.5"
                                value={prestretchPercent}
                                onChange={(e) => setPrestretchPercent(e.target.value)}
                                disabled={!prestretchEnabled}
                                className={`${styles.tensionInput} ${
                                  !prestretchEnabled ? styles.tensionInputDisabled : ""
                                }`}
                                placeholder="e.g. 10"
                              />
                              <span
                                className={`${styles.tensionUnitLabel} ${
                                  !prestretchEnabled ? styles.tensionUnitDisabled : ""
                                }`}
                              >
                                %
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className={styles.prestretchRow}>
                            <label
                              className={`${styles.prestretchLabel} ${
                                !prestretchEnabledCross ? styles.prestretchLabelDisabled : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={prestretchEnabledCross}
                                onChange={(e) => {
                                  setPrestretchEnabledCross(e.target.checked);
                                  if (!e.target.checked) setPrestretchPercentCross("");
                                }}
                                className={styles.prestretchCheckbox}
                              />
                              Cross Prestretch
                            </label>
                            <div className={styles.tensionInputRow}>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.5"
                                value={prestretchPercentCross}
                                onChange={(e) => setPrestretchPercentCross(e.target.value)}
                                disabled={!prestretchEnabledCross}
                                className={`${styles.tensionInput} ${
                                  !prestretchEnabledCross ? styles.tensionInputDisabled : ""
                                }`}
                                placeholder="e.g. 10"
                              />
                              <span
                                className={`${styles.tensionUnitLabel} ${
                                  !prestretchEnabledCross ? styles.tensionUnitDisabled : ""
                                }`}
                              >
                                %
                              </span>
                            </div>
                          </div>
                        )
                      ) : (
                        <div className={styles.prestretchRow}>
                          <label
                            className={`${styles.prestretchLabel} ${
                              !prestretchEnabled ? styles.prestretchLabelDisabled : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={prestretchEnabled}
                              onChange={(e) => {
                                setPrestretchEnabled(e.target.checked);
                                if (!e.target.checked) setPrestretchPercent("");
                              }}
                              className={styles.prestretchCheckbox}
                            />
                            Prestretch
                          </label>
                          <div className={styles.tensionInputRow}>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.5"
                              value={prestretchPercent}
                              onChange={(e) => setPrestretchPercent(e.target.value)}
                              disabled={!prestretchEnabled}
                              className={`${styles.tensionInput} ${
                                !prestretchEnabled ? styles.tensionInputDisabled : ""
                              }`}
                              placeholder="e.g. 10"
                            />
                            <span
                              className={`${styles.tensionUnitLabel} ${
                                !prestretchEnabled ? styles.tensionUnitDisabled : ""
                              }`}
                            >
                              %
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            <button
              type="button"
              className={styles.closeModalButton}
              onClick={() => setIsTensionModalOpen(false)}
            >
              {isHybrid && tensionModalTarget === "main"
                ? "Save Main Tension"
                : isHybrid && tensionModalTarget === "cross"
                ? "Save Cross Tension"
                : "Done / Close"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerBookingForm;
